import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Checkbox,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Radio,
  Row,
  Select,
} from "antd";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { colors } from "../../utils/theme";
import { device } from "../../utils/breakpoints";
import data from "../../data/hire-space-spaces.json";
import { useDispatch, useSelector } from "react-redux";
import { toggleHireSpaceModal } from "../../redux/actions/modalActions";
import StyledButton from "../StyledButton";
import Check from "../Check";
import { sendHireSpaceConfirmationEmail } from "../../functions/Api";
import {
  BOOKABLE_SPACE_OPTIONS,
  BOOKING_FLOW_SPACE_IDS,
  getExtrasForSpaces,
  MEETING_TYPE_OPTIONS,
  MEETING_TYPE_OTHER,
  SPACE_ID_FLEX_WORKSPACE,
  SPACE_ID_MAAK,
  SPACE_ID_STUDIO,
} from "../../constants/SpacesConstants";
import locale from "antd/es/date-picker/locale/nl_NL";
import dayjs from "dayjs";
import {
  getDisableDatesForMeetingRoom,
  getDisabledDatesForEndDateEvent,
} from "../../functions/Datepicker";
import * as confirmedAnimation from "../../assets/lottie/checkAnimation.json";
import Lottie from "react-lottie-player";
import {
  FIRESTORE_HIRE_REQUESTS_TABLE,
  FIRESTORE_HIRE_SPACE_EMAILS_TABLE,
} from "../../constants/FirebaseConstants";
import { useFirestore } from "react-redux-firebase";
import { HIRE_REQUEST_STATUS_OPEN } from "../../constants/HireRequestConstants";
import useIsMobile from "../Hooks/useIsMobile";

const STEP_SPACE = "space";
const STEP_ROOMS = "rooms";
const STEP_BOOKING = "booking";
const STEP_CONTACT = "contact";
const STEP_DONE = "done";

const ROOM_FIELDS = ["spaces", "extras"];

const BOOKING_FIELDS = [
  "dateFrom",
  "dateTo",
  "timePeriod",
  "meetingType",
  "meetingTypeOther",
  "guestCount",
  "catering",
];

const CONTACT_FIELDS = ["firstName", "lastName", "email", "phone"];

// Firestore weigert `undefined`; velden die de bezoeker leeg laat gaan als null mee.
const withoutUndefined = (values) =>
  Object.fromEntries(
    Object.entries(values).map(([key, value]) => [
      key,
      value === undefined ? null : value,
    ]),
  );

const HireSpaceModal = ({ preselectedSpaceId }) => {
  const { t, i18n } = useTranslation();
  const modalData = useSelector((state) => state.modalData);
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const firestore = useFirestore();
  const lang = i18n.language;
  const modalRef = useRef(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState(STEP_SPACE);
  const [chosenSpace, setChosenSpace] = useState(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");

  // De Multi en De Maak krijgen een extra stap met de boekingsgegevens;
  // Flex en Studio blijven één losse aanvraag met een open vraag.
  const isBookingFlow =
    !!chosenSpace && BOOKING_FLOW_SPACE_IDS.includes(chosenSpace.id);
  const firstStepAfterSpace = isBookingFlow ? STEP_ROOMS : STEP_CONTACT;

  const meetingType = Form.useWatch("meetingType", form);
  const dateFrom = Form.useWatch("dateFrom", form);
  // `preserve` is nodig: zonder die optie geeft useWatch `undefined` zodra de
  // velden van deze stap unmounten, en dan lijkt het alsof er geen ruimte
  // gekozen is.
  const selectedSpaces = Form.useWatch("spaces", { form, preserve: true });
  const availableExtras = useMemo(
    () => getExtrasForSpaces(selectedSpaces || []),
    [selectedSpaces],
  );

  // Ruimte uitvinken haalt ook de extra's weg die alleen daarbij hoorden.
  // Alleen opschonen zolang de ruimtestap in beeld is; daarna staat de keuze
  // vast en mag er niets meer verdwijnen.
  useEffect(() => {
    if (step !== STEP_ROOMS) return;
    const chosenExtras = form.getFieldValue("extras");
    if (!chosenExtras?.length) return;
    const stillValid = chosenExtras.filter((extra) =>
      availableExtras.includes(extra),
    );
    if (stillValid.length !== chosenExtras.length)
      form.setFieldValue("extras", stillValid);
  }, [availableExtras, step, form]);

  useEffect(() => {
    if (!!modalData && modalData.hireSpace) {
      setModalVisible(true);
      if (preselectedSpaceId) {
        const space = data[lang].find(
          (space) => space.id === preselectedSpaceId,
        );
        if (space) {
          setChosenSpace(space);
          setStep(
            BOOKING_FLOW_SPACE_IDS.includes(space.id)
              ? STEP_ROOMS
              : STEP_CONTACT,
          );
          if (BOOKING_FLOW_SPACE_IDS.includes(space.id))
            form.setFieldValue("spaces", [space.id]);
        }
      }
    } else {
      setModalVisible(false);
    }
  }, [modalData, preselectedSpaceId, lang, form]);

  const handleNext = () => {
    if (step === STEP_SPACE) {
      if (!chosenSpace) return message.warning(t("form.choose_space"));
      if (BOOKING_FLOW_SPACE_IDS.includes(chosenSpace.id))
        form.setFieldValue("spaces", [chosenSpace.id]);
      setStep(firstStepAfterSpace);
      scrollTop(modalRef);
      return;
    }
    if (step === STEP_ROOMS) {
      form.validateFields(ROOM_FIELDS).then(() => {
        setStep(STEP_BOOKING);
        scrollTop(modalRef);
      });
      return;
    }
    if (step === STEP_BOOKING) {
      form.validateFields(BOOKING_FIELDS).then(() => {
        setStep(STEP_CONTACT);
        scrollTop(modalRef);
      });
      return;
    }
    if (step === STEP_CONTACT) {
      const fieldsToValidate = isBookingFlow
        ? CONTACT_FIELDS
        : [...CONTACT_FIELDS, "occupation"];
      form.validateFields(fieldsToValidate).then(() => {
        setIsLoading(true);
        // De velden van stap 1 zijn niet meer gemount, dus lees de hele store
        // uit; validateFields levert alleen de zichtbare velden terug.
        const values = withoutUndefined(form.getFieldsValue(true));
        setName(values.firstName);
        setEmail(values.email);

        values.chosenSpace = chosenSpace.id;
        values.createdAt = dayjs().unix();
        values.status = HIRE_REQUEST_STATUS_OPEN;
        values.phone = values.phone || null;
        values.timePeriod = values.timePeriod || null;

        if (isBookingFlow) {
          // reservationDate blijft het veld waar het admin-paneel op leest.
          values.dateFrom = values.dateFrom
            ? dayjs(values.dateFrom).format("DD-MM-YYYY")
            : null;
          values.reservationDate = values.dateFrom;
          values.dateTo = values.dateTo
            ? dayjs(values.dateTo).format("DD-MM-YYYY")
            : null;
          values.spaces = values.spaces || [];
          values.extras = values.extras || [];
          values.organisation = values.organisation || null;
          values.additionalInfo = values.additionalInfo || null;
          values.meetingTypeOther = values.meetingTypeOther || null;
        } else {
          delete values.reservationDate;
        }

        sendHireSpaceConfirmationEmail(values)
          .then(() =>
            Promise.all([
              firestore
                .collection(FIRESTORE_HIRE_SPACE_EMAILS_TABLE)
                .add(values),
              firestore.collection(FIRESTORE_HIRE_REQUESTS_TABLE).add(values),
            ]),
          )
          .then(() => {
            setStep(STEP_DONE);
            scrollTop(modalRef);
            setIsLoading(false);
          })
          .catch((err) => {
            setIsLoading(false);
            message.error(t("errors.generic"));
            console.error(err);
          });
      });
    }
  };

  const handlePrevious = () => {
    if (step === STEP_CONTACT && isBookingFlow) {
      setStep(STEP_BOOKING);
      scrollTop(modalRef);
      return;
    }
    if (step === STEP_BOOKING) {
      setStep(STEP_ROOMS);
      scrollTop(modalRef);
      return;
    }
    if (step !== STEP_SPACE && !preselectedSpaceId) {
      setStep(STEP_SPACE);
      scrollTop(modalRef);
      return;
    }
    closeModal();
  };

  const scrollTop = (ref) => {
    ref.current.scrollIntoView({ behavior: "smooth" });
  };

  const closeModal = () => {
    dispatch(toggleHireSpaceModal());
    setTimeout(() => {
      setChosenSpace(null);
      setStep(STEP_SPACE);
      form.resetFields();
    }, 500);
  };

  const Choices = ({ choices }) => {
    const isMobile = useIsMobile();
    return (
      <ProductsWrapper itemAmount={choices.length} gutter={16}>
        {choices.map((item, index) => {
          return (
            <Col xs={24} md={12} lg={8} key={index}>
              <ProductContainer
                activeItem={!!chosenSpace && chosenSpace.id === item.id}
                onClick={() => setChosenSpace(item)}
              >
                {!!item.image && !isMobile && (
                  <ProductImage src={item.image} alt={item.image_alt} />
                )}
                <ProductContent>
                  <h4>{item.title}</h4>
                  <p>{item.description}</p>
                  {item.bullets.map((bullet, i) => {
                    return (
                      <Bullet key={i}>
                        <CheckWrapper>
                          <Check color={colors.header} />
                        </CheckWrapper>
                        <span>{bullet}</span>
                      </Bullet>
                    );
                  })}
                </ProductContent>
              </ProductContainer>
            </Col>
          );
        })}
      </ProductsWrapper>
    );
  };

  const spaceLabel =
    chosenSpace?.id === SPACE_ID_STUDIO
      ? t("fixed_unit")
      : chosenSpace?.id === SPACE_ID_FLEX_WORKSPACE
        ? t("flex_working")
        : chosenSpace?.id === SPACE_ID_MAAK
          ? t("maak_space")
          : t("multipurpose_space");

  return (
    <StyledModal
      title=""
      visible={modalVisible}
      centered
      onOk={() => closeModal(false)}
      onCancel={() => closeModal(false)}
      footer={null}
      width={1000}
      styles={{
        mask: {
          background: "rgba(4, 36, 16, 0.45)",
          backdropFilter: "blur(6px)",
          WebkitBackdropFilter: "blur(6px)",
        },
      }}
    >
      {/* div used to scroll to top of modal after every step */}
      <div ref={modalRef} />
      {/* Product chooser */}
      {step === STEP_SPACE && (
        <div>
          <Title>{t("hire_space_options_title")}</Title>
          <Description>{t("hire_space_options_description")}</Description>
          <Choices choices={data[lang]} />
        </div>
      )}

      {/* Stap: ruimtekeuze en extra opties */}
      {step === STEP_ROOMS && isBookingFlow && (
        <div>
          <SubTitle>{spaceLabel}</SubTitle>
          <Title>{t("booking.rooms_title")}</Title>
          <Description>{t("booking.rooms_description")}</Description>
          <StyledForm form={form} layout={"vertical"}>
            <FieldBlock>
              <Form.Item
                name="spaces"
                label={t("booking.spaces_label")}
                rules={[{ required: true, message: t("form.choose_spaces") }]}
              >
                <SpacesCheckboxGroup>
                  <Row gutter={[10, 10]}>
                    {BOOKABLE_SPACE_OPTIONS.map((option) => (
                      <Col xs={24} md={12} key={option.value}>
                        <SpaceCheckbox value={option.value}>
                          <SpaceName>{t(option.labelKey)}</SpaceName>
                          <SpaceMeta>{t(option.metaKey)}</SpaceMeta>
                        </SpaceCheckbox>
                      </Col>
                    ))}
                  </Row>
                </SpacesCheckboxGroup>
              </Form.Item>

              {!!availableExtras.length && (
                <Form.Item name="extras" label={t("booking.extras_label")}>
                  <ExtrasCheckboxGroup>
                    <Row gutter={[10, 10]}>
                      {availableExtras.map((extra) => (
                        <Col xs={12} md={8} key={extra}>
                          <ExtraCheckbox value={extra}>
                            {t(`extras.${extra}`)}
                          </ExtraCheckbox>
                        </Col>
                      ))}
                    </Row>
                  </ExtrasCheckboxGroup>
                </Form.Item>
              )}
            </FieldBlock>
          </StyledForm>
        </div>
      )}

      {/* Stap: datum, duur en soort bijeenkomst */}
      {step === STEP_BOOKING && isBookingFlow && (
        <div>
          <SubTitle>{spaceLabel}</SubTitle>
          <Title>{t("booking.request_title")}</Title>
          <Description>{t("booking.request_description")}</Description>
          <StyledForm form={form} layout={"vertical"}>
            <FieldBlock>
              <Row gutter={12}>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="dateFrom"
                    label={t("booking.date_from_label")}
                    rules={[{ required: true, message: t("form.choose_date") }]}
                  >
                    <DatePicker
                      className={"w-100"}
                      locale={locale}
                      format={"DD-MM-YYYY"}
                      placeholder={t("choose_date")}
                      showToday={false}
                      inputReadOnly={true}
                      disabledDate={getDisableDatesForMeetingRoom}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item name="dateTo" label={t("booking.date_to_label")}>
                    <DatePicker
                      className={"w-100"}
                      locale={locale}
                      format={"DD-MM-YYYY"}
                      placeholder={t("choose_date")}
                      showToday={false}
                      inputReadOnly={true}
                      disabledDate={(current) =>
                        getDisableDatesForMeetingRoom(current) ||
                        (!!dateFrom &&
                          getDisabledDatesForEndDateEvent(dateFrom, current))
                      }
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                name="timePeriod"
                label={t("booking.day_part_label")}
                rules={[{ required: true, message: t("form.choose_day_part") }]}
              >
                <ChoiceRadioGroup>
                  <ChoiceRadio value="half_day">{t("half_day")}</ChoiceRadio>
                  <ChoiceRadio value="whole_day">{t("whole_day")}</ChoiceRadio>
                </ChoiceRadioGroup>
              </Form.Item>

              <Row gutter={12}>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="guestCount"
                    label={t("booking.guest_count_label")}
                    rules={[
                      { required: true, message: t("form.enter_guest_count") },
                    ]}
                  >
                    <InputNumber
                      className={"w-100"}
                      min={1}
                      max={500}
                      placeholder={t("booking.guest_count_placeholder")}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="meetingType"
                    label={t("booking.meeting_type_label")}
                    rules={[
                      {
                        required: true,
                        message: t("form.choose_meeting_type"),
                      },
                    ]}
                  >
                    <Select placeholder={t("booking.meeting_type_placeholder")}>
                      {MEETING_TYPE_OPTIONS.map((option) => (
                        <Select.Option value={option} key={option}>
                          {t(`meeting_type.${option}`)}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              {meetingType === MEETING_TYPE_OTHER && (
                <Form.Item
                  name="meetingTypeOther"
                  label={t("booking.meeting_type_other_label")}
                  rules={[
                    {
                      required: true,
                      message: t("form.describe_meeting_type"),
                    },
                  ]}
                >
                  <Input
                    placeholder={t("booking.meeting_type_other_placeholder")}
                  />
                </Form.Item>
              )}

              <Form.Item
                name="catering"
                label={t("booking.catering_label")}
                rules={[{ required: true, message: t("form.choose_catering") }]}
              >
                <ChoiceRadioGroup>
                  <ChoiceRadio value="yes">
                    {t("booking.catering_yes")}
                  </ChoiceRadio>
                  <ChoiceRadio value="no">
                    {t("booking.catering_no")}
                  </ChoiceRadio>
                </ChoiceRadioGroup>
              </Form.Item>
            </FieldBlock>
          </StyledForm>
        </div>
      )}

      {/*Contact info form*/}
      {step === STEP_CONTACT && !!chosenSpace && (
        <div>
          <SubTitle>{spaceLabel}</SubTitle>
          <Title>
            {isBookingFlow
              ? t("booking.details_title")
              : t("hire_space_form_title")}
          </Title>
          <Description>
            {isBookingFlow
              ? t("booking.details_description")
              : t("hire_space_form_description")}
          </Description>
          <StyledForm form={form} layout={"vertical"}>
            <FieldBlock>
              <Row gutter={12}>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="firstName"
                    label={t("first_name")}
                    rules={[
                      { required: true, message: t("form.enter_first_name") },
                    ]}
                  >
                    <Input />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="lastName"
                    label={t("last_name")}
                    rules={[
                      { required: true, message: t("form.enter_last_name") },
                    ]}
                  >
                    <Input />
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={12}>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="email"
                    label={t("email")}
                    rules={[
                      { required: true, message: t("form.enter_email") },
                      { type: "email", message: t("form.enter_valid_email") },
                    ]}
                  >
                    <Input />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item name="phone" label={t("phone_number")}>
                    <Input />
                  </Form.Item>
                </Col>
              </Row>
              {isBookingFlow ? (
                <>
                  <Form.Item
                    name="organisation"
                    label={t("booking.organisation_label")}
                  >
                    <Input />
                  </Form.Item>
                  <Form.Item
                    name="additionalInfo"
                    label={t("booking.additional_info_label")}
                  >
                    <Input.TextArea
                      rows={4}
                      placeholder={t("booking.additional_info_placeholder")}
                    />
                  </Form.Item>
                </>
              ) : (
                <Form.Item
                  name="occupation"
                  label={t("occupation_form_question")}
                  rules={[
                    { required: true, message: t("form.enter_occupation") },
                  ]}
                >
                  <Input.TextArea
                    rows={4}
                    placeholder={t("occupation_form_placeholder")}
                  />
                </Form.Item>
              )}
            </FieldBlock>
          </StyledForm>
        </div>
      )}

      {/* Confirmation Screen */}
      {step === STEP_DONE && (
        <div className={"p-lg-5"}>
          <LottieWrapper>
            <Lottie animationData={confirmedAnimation} play />
          </LottieWrapper>
          <Title>{t("hire_space_confirm_title", { name: name })}</Title>
          <SubTitle>
            {t("hire_space_confirm_description", { email: email })}
          </SubTitle>
        </div>
      )}
      {/* Go back and next buttons OR continue shopping & go to cart buttons */}
      {step !== STEP_DONE ? (
        <ButtonContainer>
          <StepBackButton onClick={handlePrevious}>
            {t("go_back")}
          </StepBackButton>
          <StyledButton small onClick={handleNext} loading={isLoading}>
            {t("next")}
          </StyledButton>
        </ButtonContainer>
      ) : (
        <ButtonContainer>
          <StyledButton small onClick={closeModal}>
            {t("finish")}
          </StyledButton>
        </ButtonContainer>
      )}
    </StyledModal>
  );
};

const FieldBlock = styled.div`
  background: rgba(255, 252, 245, 0.55);
  border: 1px solid rgba(30, 92, 50, 0.12);
  border-radius: 10px;
  padding: 20px 18px 4px;

  @media ${device.lg} {
    padding: 24px 24px 6px;
  }

  .ant-form-item {
    margin-bottom: 18px;
  }

  .ant-form-item-label {
    padding-bottom: 2px;

    label {
      font-size: 12.5px;
      font-weight: 600;
      line-height: 1.3;
      height: auto;
      color: ${colors.header};
    }
  }
`;

const SpacesCheckboxGroup = styled(Checkbox.Group)`
  display: grid;
  gap: 6px;
  width: 100%;
`;

const SpaceCheckbox = styled(Checkbox)`
  display: flex;
  align-items: flex-start;
  width: 100%;
  margin: 0;
  padding: 8px 12px;
  border: 1px solid #b0b0b0;
  border-radius: 6px;
  background: transparent;
  transition: 0.2s all ease;

  &:hover {
    border-color: ${colors.header};
  }

  &.ant-checkbox-wrapper-checked {
    border-color: ${colors.header};
    background: rgba(241, 232, 216, 0.7);
  }

  .ant-checkbox {
    top: 3px;
  }

  > span:last-child {
    padding-inline-end: 0;
  }
`;

const SpaceName = styled.div`
  font-size: 13px;
  font-weight: 700;
  line-height: 1.25;
  color: ${colors.header};
`;

const SpaceMeta = styled.div`
  font-size: 11px;
  line-height: 1.35;
  color: #5c6353;
  margin-top: 2px;
  white-space: normal;
`;

const ExtrasCheckboxGroup = styled(Checkbox.Group)`
  display: block;
  width: 100%;
`;

const ExtraCheckbox = styled(Checkbox)`
  display: flex;
  align-items: center;
  width: 100%;
  margin: 0;
  padding: 8px 10px;
  border: 1px solid #b0b0b0;
  border-radius: 6px;
  font-size: 12px;
  transition: 0.2s all ease;

  &.ant-checkbox-wrapper-checked {
    border-color: ${colors.header};
    background: rgba(241, 232, 216, 0.5);
  }

  > span:last-child {
    white-space: normal;
    line-height: 1.3;
  }
`;

const ChoiceRadioGroup = styled(Radio.Group)`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const ChoiceRadio = styled(Radio)`
  flex: 1 1 130px;
  align-items: center;
  margin: 0;
  padding: 10px 12px;
  border: 1px solid #b0b0b0;
  border-radius: 6px;
  transition: 0.2s all ease;

  &.ant-radio-wrapper-checked {
    border-color: ${colors.header};
    background: rgba(241, 232, 216, 0.5);
  }
`;

const LottieWrapper = styled.div`
  display: flex;
  width: 100%;
  justify-content: center;
  svg {
    max-width: 130px;
    @media ${device.lg} {
      max-width: 200px;
    }
  }
`;

const StyledForm = styled(Form)`
  input,
  textarea {
    background-color: transparent !important;
    border: 1px solid #b0b0b0;
  }

  .ant-picker,
  .ant-input-number,
  .ant-select:not(.ant-select-customize-input) .ant-select-selector {
    background-color: transparent !important;
    border: 1px solid #b0b0b0;
  }

  .ant-picker input,
  .ant-input-number input {
    border: none;
  }

  .ant-input-number-handler-wrap {
    background: transparent;
  }

  .ant-select-selection-placeholder,
  .ant-input-number-input::placeholder,
  input::placeholder,
  textarea::placeholder {
    color: rgba(59, 63, 51, 0.5);
  }

  @media ${device.lg} {
    padding: 2rem 3rem;
  }
`;

const CheckWrapper = styled.div`
  min-width: 24px;
  margin-right: 0.75rem;
`;

const Bullet = styled.div`
  text-align: start;
  display: flex;
  margin-bottom: 0.4rem;
  align-items: center;

  .anticon {
    font-size: 24px;
    color: green;
    margin-right: 0.4rem;
  }

  span {
    line-height: 18px;
  }
`;

const ProductImage = styled.img`
  max-width: 100%;
  min-height: 172px;
  border-radius: 10px;
`;

const ProductsWrapper = styled(Row)`
  display: flex;
  padding: 1rem 0 1rem 0;
  justify-content: center;

  @media ${device.lg} {
    justify-content: ${({ itemAmount }) =>
      itemAmount < 3 ? "center" : "space-between"};
  }
`;

const ProductContent = styled.div`
  padding: 1rem;
`;

const ProductContainer = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: 10px;
  transition: 0.3s all ease;
  border: 1px solid lightgrey;
  margin-bottom: 1rem;
  ${({ activeItem }) =>
    activeItem &&
    `
      box-shadow: 0px 5px 24px rgb(0 0 0 / 30%);
      background-color: rgb(241,232,216, 0.5)
    `};

  &:hover,
  &.active,
  &.overview {
    box-shadow: 0px 2px 24px rgb(0 0 0 / 15%);
    cursor: pointer;
  }

  h4 {
    font-size: 24px;
    font-weight: 600;
    color: ${colors.header};
  }

  p {
    font-size: 14px;
    line-height: 22px;
  }

  @media ${device.maxSm} {
    margin-bottom: 2rem;
  }
`;

const StepBackButton = styled.button`
  background: transparent;
  align-self: center;
  border: none;
  color: ${colors.primary};
  font-weight: 500;
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  padding: 1rem 0 1rem 0;

  & button {
    margin: 0;
  }
`;

const SubTitle = styled.h4`
  font-size: 15px;
  font-weight: 600;
  text-transform: uppercase;
  text-align: center;
`;

const Description = styled.p`
  font-size: 15px;
  text-align: center;
  white-space: pre-line;
`;

const Title = styled.h3`
  font-size: 24px;
  font-weight: 600;
  color: ${colors.header};
  text-align: center;
  margin: 1rem 0;

  @media ${device.lg} {
    font-size: 30px;
  }
`;

const StyledModal = styled(Modal)`
  .ant-modal-content {
    background-color: ${colors.light};
  }
`;

export default HireSpaceModal;
